import { useExportIntegrationsMutation, useGetIntegrationsQuery } from "api/TenantSlug/Integration/hooks.ts";
import { AddApiIntegrationDrawer } from "components/Drawers/AddApiIntegrationDrawer/AddApiIntegrationDrawer";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell.tsx";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { LabelsCell } from "components/UI/Table/UI/LabelsCell/LabelsCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useRecoilValue } from "recoil";
import { IntegrationStatus, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models.ts";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";

import IntegrationData = App.Data.IntegrationData;
import GetIntegrationsResponse = App.Http.Responses.Integration.GetIntegrationsResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

export const ApiIntegrationsTable = () => {
    const tableId = TableId.ApiIntegrationsTable;
    const tableAtomInstance = tableAtom<IntegrationData>();
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const permissionsUtils = usePermission();
    const exportState = useRecoilValue(exportAtom(tableId));
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const request = {
        filter: {
            filter: {
                ...(tableState.filter?.filter || {}),
            },
            sort: tableState?.filter?.sort ?? "name",
            include: "owner,offers,formStructure,country,category,labels",
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };
    const getIntegrationsQuery = useGetIntegrationsQuery(request, { enabled: false });

    const exportIntegrationsMutation = useExportIntegrationsMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetIntegrationsResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, getIntegrationsQuery, undefined, undefined, exportIntegrationsMutation);

    const columnConfig: TableColumnType<IntegrationData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "name",
            key: "name",
            inputType: "text",
            editable: true,
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        /* {
            title: <ColumnHeaderTitle columnTitle={t("columns.date")} />,
            dataIndex: "date",
            key: "date",
            inputType: "date",
            editable: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.offer")} />,
            dataIndex: "offer",
            key: "offer",
            inputType: "text",
            editable: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.advertiser")} />,
            dataIndex: "advertiser",
            key: "advertiser",
            inputType: "text",
            editable: true,
        },*/
        {
            title: <ColumnHeaderTitle columnTitle={tG("structure")} />,
            dataIndex: "formStructure.name",
            key: "formStructure.name",
            render: (_, record) => <EntityLinkCell.FormStructure entity={record.form_structure ?? undefined} />,
            sorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        TableColumn.Entity.Advertiser<IntegrationData>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            dataIndex: "owner.nickname",
            key: "owner.nickname",
            filterType: FILTER_TYPES.advertiserSelect,
            filterName: "owner_id",
            sortName: "owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (record) => record.owner,
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "country_id",
            key: "country_id",
            inputType: "text",
            render: (_, data: IntegrationData) => <CountryCell country={data.country} />,
            sorter: true,
            width: 100,
            filterable: true,
            filterType: FILTER_TYPES.countrySelect,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("category")} />,
            dataIndex: "category.name",
            key: "category.name",
            render: (_, data: IntegrationData) => data.category?.name ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.categorySelect,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "status",
            key: "status",
            inputType: "text",
            filterable: true,
            filterType: FILTER_TYPES.string,
            editable: true,
            render: (status: IntegrationStatus) => capitalizeFirstLetter(status),
            sorter: true,
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("tags")} />,
            dataIndex: "labels.name",
            key: "labels.name",
            render: (_, data) => <LabelsCell labels={data.labels} />,
            filterable: true,
            filterType: FILTER_TYPES.labelMultiSelect,
            ellipsis: false,
            width: 150,
        },
    ];

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<IntegrationData>({
        entityName: "integration",
        editPermission: PermissionEnum.IntegrationUpdate,
        detailPermission: PermissionEnum.IntegrationView,
        editUrl: "/$tenantSlug/tool/scenario/integration/:id/general",
    });

    return (
        <>
            {permissionsUtils.hasPermission(PermissionEnum.IntegrationCreate) && (
                <AddApiIntegrationDrawer onSubmit={() => tableFetchDataUtils.refetchData()} />
            )}
            <Table<GetIntegrationsResponse, IntegrationData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetIntegrationsResponse}
                dataSource={tableFetchDataUtils.tableData?.integrations || []}
                columnConfig={columnConfig}
                loading={getIntegrationsQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                addEntityData={{
                    drawerId: DrawerId.AddApiIntegration,
                    permission: PermissionEnum.IntegrationCreate,
                }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                /* @ts-ignore */
                rowKey={(record) => record.id}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                hideActionsColumn={true}
                autoScrollWidth={true}
                enableFilters={true}
                searchDataIndex="name"
            />
        </>
    );
};
